import { Star } from 'lucide-react';
import { useState } from 'react';

import { type DevFlag, type FlagRawValue } from '@/features/flags-dev/discovery';
import { toggleFavourite } from '@/features/flags-dev/favourites';
import {
  type FlagOverrideValue,
  createVariantOverride,
  getVariantOverride,
  setOverride,
} from '@/features/flags-dev/overrides';
import { Button } from '@heidi/design-system/button';
import { CopyButton } from '@heidi/design-system/copy-button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { ToggleGroup, ToggleGroupItem } from '@heidi/design-system/toggle-group';
import { TypographyCaption, typographyVariant } from '@heidi/design-system/typography';

type ControlValue = FlagOverrideValue | 'default';

const TRI_STATE_OPTIONS: { value: ControlValue; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'default', label: 'Default' },
  { value: 'on', label: 'On' },
];

const formatRawValue = (value: FlagRawValue): string => {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const formatOverrideValue = (value: FlagOverrideValue): string =>
  getVariantOverride(value) ?? value;

export const FlagRow = ({ flag, favourite }: { flag: DevFlag; favourite: boolean }) => {
  const controlValue: ControlValue = flag.override ?? 'default';
  // 700+ rows mount at once, so the CopyButton (and its Radix Tooltip) mounts
  // only on hover/focus, and the key uses a native <label> rather than the DS
  // Label — clicking the key text forwards to the button inside it.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const showCopy = hovered || focused;

  return (
    <div
      className="flex flex-col gap-1.5"
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-0.5">
          <label
            className="-my-0.5 flex min-h-6 cursor-pointer items-center gap-1"
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
          >
            <span
              className={typographyVariant({
                variant: 'p2Bold',
                className: 'min-w-0 break-all font-mono text-text-primary',
              })}
            >
              {flag.key}
            </span>
            {showCopy ? (
              <CopyButton
                type="button"
                size="xxs"
                onCopy={() => navigator.clipboard.writeText(flag.key)}
                label={`Copy ${flag.key}`}
                copiedLabel={`Copied ${flag.key}`}
                className="shrink-0"
              />
            ) : (
              // Reserves the button's width so a break-all key doesn't reflow on mount.
              <span aria-hidden className="size-6 shrink-0" />
            )}
          </label>
          {flag.description ? (
            <TypographyCaption className="text-text-tertiary">
              {flag.description}
            </TypographyCaption>
          ) : null}
          <TypographyCaption className="text-text-secondary">
            Provider value:{' '}
            <span className="font-mono tabular-nums">
              {formatRawValue(flag.rawValue)}
            </span>
            {flag.override ? (
              <>
                {' '}
                · Override:{' '}
                <span className="font-mono">{formatOverrideValue(flag.override)}</span>
              </>
            ) : null}
          </TypographyCaption>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="xxs"
          iconOnly
          onClick={() => toggleFavourite(flag.provider, flag.key)}
          aria-label={favourite ? `Unfavourite ${flag.key}` : `Favourite ${flag.key}`}
          aria-pressed={favourite}
          data-testid={`dev-tools-feature-flags-favourite-${flag.provider}-${flag.key}`}
          className="shrink-0"
        >
          <Star
            aria-hidden
            className={favourite ? 'text-text-primary' : 'text-text-tertiary'}
            fill={favourite ? 'currentColor' : 'none'}
          />
        </Button>
      </div>
      {flag.variants?.length ? (
        <Select
          density="compact"
          value={controlValue}
          onValueChange={(next) => {
            setOverride(
              flag.provider,
              flag.key,
              next === 'default' ? undefined : (next as FlagOverrideValue)
            );
          }}
        >
          <SelectTrigger
            className="w-full"
            aria-label={`Override ${flag.key}`}
            data-testid={`dev-tools-feature-flags-variant-${flag.provider}-${flag.key}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[10001]">
            <SelectItem value="default">Default</SelectItem>
            <SelectItem value="off">Off</SelectItem>
            <SelectItem value="on">On (provider variant)</SelectItem>
            {flag.variants.map((variant) => (
              <SelectItem key={variant} value={createVariantOverride(variant)}>
                Variant: {variant}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        // Not SegmentedControl: that is one observer per row when search mounts hundreds.
        <ToggleGroup
          type="single"
          value={controlValue}
          onValueChange={(next) => {
            // Radix emits '' when the active item is clicked again — keep the current value.
            if (!next) return;
            const v = next as ControlValue;
            setOverride(flag.provider, flag.key, v === 'default' ? undefined : v);
          }}
          aria-label={`Override ${flag.key}`}
          className="grid w-full grid-cols-3 gap-0 rounded-lg bg-background-tertiary p-1"
        >
          {TRI_STATE_OPTIONS.map((opt) => (
            <ToggleGroupItem
              key={opt.value}
              value={opt.value}
              className="h-7 rounded-md px-2.5 font-normal text-text-secondary hover:bg-transparent hover:text-text-primary data-[state=on]:bg-surface data-[state=on]:text-text-primary data-[state=on]:shadow-sm"
            >
              {opt.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}
    </div>
  );
};
